import { useState, useCallback, useEffect } from 'react';

interface EyeDropperResult {
  sRGBHex: string;
}

interface EyeDropper {
  open: () => Promise<EyeDropperResult>;
}

declare global {
  interface Window {
    EyeDropper?: {
      new (): EyeDropper;
    };
  }
}

interface UseEyeDropperReturn {
  /** Whether the EyeDropper API is supported in the current browser */
  isSupported: boolean;
  /** Whether the eyedropper is currently active */
  isOpen: boolean;
  /** Error message if the eyedropper operation failed */
  error: string | null;
  /** Open the eyedropper and return the selected color */
  open: () => Promise<string | null>;
}

/**
 * Custom hook to interact with the browser's EyeDropper API.
 * 
 * The EyeDropper API allows users to select colors from anywhere on their screen,
 * including other browser tabs, applications, and the desktop.
 * 
 * Browser support:
 * - Chrome/Edge 95+
 * - Opera 81+
 * - Not yet supported in Firefox or Safari (as of 2024)
 * 
 * @returns Object with isSupported flag, isOpen state, error state, and open function
 * 
 * @example
 * ```tsx
 * const { isSupported, open, error } = useEyeDropper();
 * 
 * const handleClick = async () => {
 *   const color = await open();
 *   if (color) {
 *     console.log('Selected color:', color);
 *   }
 * };
 * ```
 */
export function useEyeDropper(): UseEyeDropperReturn {
  const [isSupported, setIsSupported] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if EyeDropper API is available
    // Defer state update to avoid synchronous setState in effect
    setTimeout(() => {
      setIsSupported('EyeDropper' in window);
    }, 0);
  }, []);

  const open = useCallback(async (): Promise<string | null> => {
    if (!isSupported) {
      setError('EyeDropper API is not supported in this browser. Try Chrome, Edge, or Opera.');
      return null;
    }

    setIsOpen(true);
    setError(null);

    try {
      const eyeDropper = new window.EyeDropper!();
      const result = await eyeDropper.open();
      setIsOpen(false);
      return result.sRGBHex;
    } catch (err) {
      setIsOpen(false);
      
      // User cancelled the operation
      if (err instanceof Error && err.name === 'AbortError') {
        // This is expected behavior when user cancels, don't set error
        return null;
      }

      // Other errors
      const errorMessage = err instanceof Error ? err.message : 'Failed to open eyedropper';
      setError(errorMessage);
      console.error('EyeDropper error:', err);
      return null;
    }
  }, [isSupported]);

  return {
    isSupported,
    isOpen,
    error,
    open,
  };
}
